// apps/admin/src/pages/admin/MarketAnalysisTab.js
// Executive Market Analysis, Competitor Benchmarking & Intelligence Center for Super Admin

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getStrategyAnalysis,
  reanalyzeStrategy,
  toggleChecklistItem,
} from "../../services/marketing";
import "./MarketAnalysisTab.css";

const COMPETITORS = [
  {
    name: "Ownezzy",
    highlight: true,
    model: "SaaS (Flat Monthly)",
    price: "₹299 – ₹499 / mo",
    commission: "0% (Direct UPI)",
    setupTime: "60 Seconds",
    blankCanvas: "None (20 niche stores pre-loaded)",
    indiaUx: "OTP login, direct UPI, WhatsApp alerts, COD",
    logistics: "Manual / Flat rate (Shiprocket planned)",
    target: "Instagram / WhatsApp social sellers & boutiques",
    vulnerability: "Pre-launch brand awareness; logistics API needed",
  },
  {
    name: "Shopify India",
    highlight: false,
    model: "Global Enterprise SaaS",
    price: "₹1,999 – ₹7,499 / mo",
    commission: "2.0% transaction fee + app costs",
    setupTime: "3 – 7 Days",
    blankCanvas: "Severe (Empty canvas, requires coding/designer)",
    indiaUx: "Requires paid apps for OTP, COD fees & WhatsApp",
    logistics: "Complete (Multiple 3rd-party apps)",
    target: "Funded D2C brands & large retail stores",
    vulnerability: "Too costly, complex, and intimidating for micro-sellers",
  },
  {
    name: "Dukaan",
    highlight: false,
    model: "Indian SaaS",
    price: "₹2,000 – ₹5,000+ / mo",
    commission: "0% – 1.5% depending on tier",
    setupTime: "1 – 2 Days",
    blankCanvas: "Moderate (Templates are generic e-commerce)",
    indiaUx: "Good Indian payment & OTP flows",
    logistics: "Integrated Dukaan Delivery",
    target: "Scaling D2C & cross-border businesses",
    vulnerability: "Pivoted away from micro-sellers; expensive plans",
  },
  {
    name: "Bikayi",
    highlight: false,
    model: "WhatsApp Catalog / SaaS",
    price: "Freemium to ₹1,500 / mo",
    commission: "Payment gateway charges",
    setupTime: "1 – 3 Hours",
    blankCanvas: "High (Basic catalog listing)",
    indiaUx: "Strong WhatsApp integration",
    logistics: "Basic courier integrations",
    target: "WhatsApp catalog sellers",
    vulnerability: "Confusing pricing tiers; sterile storefront designs",
  },
  {
    name: "Swiggy Minis / Instamojo",
    highlight: false,
    model: "Transactional Storefront",
    price: "₹0 setup",
    commission: "3% – 5% per transaction",
    setupTime: "1 – 2 Hours",
    blankCanvas: "High (Generic standardized pages)",
    indiaUx: "Good payment gateways",
    logistics: "Self-ship or basic partner",
    target: "Food creators, small artisans",
    vulnerability: "Transaction cut eats seller margins; no brand identity",
  },
  {
    name: "Amazon / Flipkart / Meesho",
    highlight: false,
    model: "Marketplace",
    price: "₹0 listing fee",
    commission: "15% – 30% per sale",
    setupTime: "Days (GST & approval)",
    blankCanvas: "N/A (Standardized product grid)",
    indiaUx: "High consumer trust",
    logistics: "Fulfilled by marketplace",
    target: "Wholesale manufacturers & resellers",
    vulnerability: "Customer data hidden, 25%+ cuts, return fraud risks",
  },
  {
    name: "Instagram DM + UPI QR (Manual)",
    highlight: false,
    model: "Manual Baseline",
    price: "₹0",
    commission: "0%",
    setupTime: "Immediate",
    blankCanvas: "N/A (Chat messages)",
    indiaUx: "Manual screenshot verification",
    logistics: "Book by hand at local post office",
    target: "15M+ Home sellers & hobbyists",
    vulnerability: "DM exhaustion, lost orders, ghosting, unorganized notebook accounting",
  },
];

const ACTION_PHASES = [
  {
    phase: "Phase 1: Quick Wins & Immediate Conversion",
    timing: "Weeks 1 – 2",
    tag: "High Priority",
    items: [
      {
        key: "action_trial_followups",
        title: "Deploy Automated Trial Follow-ups on WhatsApp (Day 1, 3, 6)",
        desc: "Reach out to free-trial owners based on signals: never logged in, no custom products added, or pending password.",
        badge: "Completed in API",
        defaultChecked: true,
      },
      {
        key: "action_shiprocket_api",
        title: "Integrate Shiprocket / NimbusPost 1-Click Courier Booking",
        desc: "Enable sellers to calculate live courier rates by pin code, print shipping labels, and auto-assign AWB numbers.",
        badge: "Engineering (3-4 days)",
        defaultChecked: false,
      },
      {
        key: "action_dynamic_upi",
        title: "Implement Frictionless Dynamic UPI QR for Unregistered Sellers",
        desc: "Allow home entrepreneurs without GST/Razorpay KYC to accept instant UPI payments directly to their bank account.",
        badge: "High Impact",
        defaultChecked: false,
      },
    ],
  },
  {
    phase: "Phase 2: Operational Ease & Seller Retention",
    timing: "Weeks 3 – 4",
    tag: "Retention Engine",
    items: [
      {
        key: "action_order_alarm",
        title: "Loud Order Ring Alarm on Admin Web / Mobile",
        desc: "Sound a distinct cash-register or phone ring when a new order arrives, alerting shopkeepers instantly even if phone is locked.",
        badge: "Seller Delight",
        defaultChecked: false,
      },
      {
        key: "action_cart_recovery",
        title: "15-Minute WhatsApp Abandoned Cart Recovery Trigger",
        desc: "Automatically message shoppers who filled their number but dropped off at payment with a 1-tap direct checkout link.",
        badge: "+22% Sales Boost",
        defaultChecked: false,
      },
      {
        key: "action_annual_plan",
        title: "Launch Annual Plan Discount (₹2,999/yr) at Trial Expiry",
        desc: "Offer 35% savings + free custom domain linking on Day 6 of free trial to lock in upfront annual recurring revenue.",
        badge: "ARR Booster",
        defaultChecked: false,
      },
    ],
  },
  {
    phase: "Phase 3: Scale, Distribution & Virality",
    timing: "Month 2+",
    tag: "Growth Flywheel",
    items: [
      {
        key: "action_android_app",
        title: "Launch Android Seller App (TWA Wrapper on Google Play)",
        desc: "Deploy lightweight Trusted Web Activity with native push notifications, biometric login, and camera photo uploader.",
        badge: "Mass Adoption",
        defaultChecked: false,
      },
      {
        key: "action_viral_footer",
        title: "'Create a Store Like This in 60s' Viral Footer Badge",
        desc: "Add discreet but high-converting referral badge on all client storefronts that clones the exact niche template on tap.",
        badge: "Organic K-Factor",
        defaultChecked: false,
      },
      {
        key: "action_ig_catalog_sync",
        title: "1-Click Product Importer from Instagram Feed / WhatsApp",
        desc: "Let new sellers enter their Instagram handle or upload their price list to auto-create categories and products in 30 seconds.",
        badge: "Zero Friction",
        defaultChecked: false,
      },
    ],
  },
];

export default function MarketAnalysisTab({ notify, user }) {
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [checklist, setChecklist] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getStrategyAnalysis();
      if (res?.data?.success && res.data.data) {
        setAnalysisData(res.data.data);
        if (res.data.data.checklist) {
          setChecklist(res.data.data.checklist);
        }
      }
    } catch {
      // Fallback gracefully
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleReanalyze = async () => {
    try {
      setReanalyzing(true);
      const res = await reanalyzeStrategy();
      if (res?.data?.success) {
        notify?.(res.data.message || "Market analysis and intelligence updated successfully!", "success");
        await loadData();
      }
    } catch {
      notify?.("Failed to refresh market analysis", "error");
    } finally {
      setReanalyzing(false);
    }
  };

  const handleToggleTask = async (itemKey) => {
    const nextVal = !checklist[itemKey];
    setChecklist((prev) => ({ ...prev, [itemKey]: nextVal }));
    try {
      await toggleChecklistItem(itemKey, nextVal);
      notify?.(nextVal ? "Action item marked complete" : "Action item reopened", "info");
    } catch {
      // Revert if API fails
      setChecklist((prev) => ({ ...prev, [itemKey]: !nextVal }));
    }
  };

  const score = analysisData?.healthScore || 96;
  const lastAnalyzed = useMemo(() => {
    if (!analysisData?.lastAnalyzedAt) return "Just now";
    try {
      return new Date(analysisData.lastAnalyzedAt).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Just now";
    }
  }, [analysisData?.lastAnalyzedAt]);

  const showSection = (sectionKey) => activeFilter === "all" || activeFilter === sectionKey;

  return (
    <div className="market-analysis-shell">
      {/* 1. Master Header Card */}
      <section className="ma-header-banner">
        <div className="ma-header-main">
          <div className="ma-badge-row">
            <span className="ma-badge-tag">Super Admin Executive Report</span>
            <span className="ma-score-badge">
              <span>★</span> Market Readiness: {score}/100
            </span>
            <span className="ma-badge-tag">Indian D2C &amp; Social Commerce</span>
          </div>
          <h2 className="ma-title">Ownezzy Market Analysis &amp; Strategic Intelligence</h2>
          <p className="ma-subtitle">
            Comprehensive market opportunity breakdown, competitor benchmarking, SWOT evaluation,
            product gaps, and hyper-targeted growth playbooks for 2026–2027.
          </p>
        </div>

        <div className="ma-header-actions">
          <button
            type="button"
            className="btn-reanalyze-ma"
            onClick={handleReanalyze}
            disabled={reanalyzing}
            title="Re-run market scans and refresh competitive metrics"
          >
            {reanalyzing ? (
              <>
                <span className="spinner-icon">🔄</span>
                <span>Analyzing Market Signals…</span>
              </>
            ) : (
              <>
                <span>🔄</span>
                <span>Re-Analyze Market</span>
              </>
            )}
          </button>
          <div className="ma-last-analyzed">Last Intelligence Update: {lastAnalyzed}</div>
        </div>
      </section>

      {/* 2. Sub-Navigation Filter Bar */}
      <div className="ma-nav-bar" role="tablist" aria-label="Market Analysis Sections">
        {[
          { key: "all", label: "📑 All Intelligence" },
          { key: "analysis", label: "📊 Market Opportunity" },
          { key: "competitors", label: "⚔️ Competitors Matrix" },
          { key: "swot", label: "⚖️ Strengths & Weaknesses" },
          { key: "improvements", label: "🛠️ Improvements & Solutions" },
          { key: "actions", label: "✅ Immediate Action Items" },
          { key: "recommendations", label: "💡 Strategic Suggestions" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ma-nav-btn ${activeFilter === tab.key ? "active" : ""}`}
            onClick={() => setActiveFilter(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 3. KPI Summary Bar */}
      <div className="ma-kpi-grid">
        <div className="ma-kpi-card">
          <span className="ma-kpi-label">
            <span>Total Addressable Market</span>
            <span>🇮🇳</span>
          </span>
          <span className="ma-kpi-value">15,000,000+</span>
          <span className="ma-kpi-sub">Social sellers across India</span>
        </div>

        <div className="ma-kpi-card">
          <span className="ma-kpi-label">
            <span>Beachhead Target</span>
            <span>🎯</span>
          </span>
          <span className="ma-kpi-value">2,800,000</span>
          <span className="ma-kpi-sub">High-margin Instagram D2C sellers</span>
        </div>

        <div className="ma-kpi-card">
          <span className="ma-kpi-label">
            <span>Projected CAC</span>
            <span>📉</span>
          </span>
          <span className="ma-kpi-value">₹145 – ₹175</span>
          <span className="ma-kpi-sub">Via laser-targeted Reels campaigns</span>
        </div>

        <div className="ma-kpi-card">
          <span className="ma-kpi-label">
            <span>Sales Commission</span>
            <span>💰</span>
          </span>
          <span className="ma-kpi-value">0% Flat</span>
          <span className="ma-kpi-sub">Direct UPI to seller bank account</span>
        </div>

        <div className="ma-kpi-card">
          <span className="ma-kpi-label">
            <span>Setup Velocity</span>
            <span>⚡</span>
          </span>
          <span className="ma-kpi-value">&lt; 60 Seconds</span>
          <span className="ma-kpi-sub">Zero Blank Canvas with 20 demo stores</span>
        </div>

        <div className="ma-kpi-card">
          <span className="ma-kpi-label">
            <span>Payback Period</span>
            <span>⏱️</span>
          </span>
          <span className="ma-kpi-value">&lt; 21 Days</span>
          <span className="ma-kpi-sub">At ₹299/mo plan conversion</span>
        </div>
      </div>

      {/* 4. Section: Current Market Analysis & Future Potential */}
      {showSection("analysis") && (
        <section className="ma-section">
          <div className="ma-section-header">
            <div className="ma-section-title-wrap">
              <h3><span>📊</span> Current Market Analysis &amp; Future Potential</h3>
              <p>The macroeconomic shift driving Indian micro-sellers from DM chaos to independent storefronts.</p>
            </div>
            <span className="ma-pill-good">$16B+ Market by 2027 (55% CAGR)</span>
          </div>

          <div className="ma-trends-grid">
            <div className="ma-trend-box">
              <div className="ma-trend-box-header">
                <span className="ma-trend-icon">😫</span>
                <span className="ma-trend-title">DM Fatigue &amp; Burnout</span>
              </div>
              <p className="ma-trend-desc">
                Solo entrepreneurs and home-based women sellers spend 4–6 hours daily typing
                &ldquo;Price please? Check DM&rdquo;, manually copy-pasting UPI QR codes, and tracking
                orders on paper notebooks while managing household duties.
              </p>
              <div className="ma-trend-stat">Ownezzy Fix: 1 bio link automates 100% of order collection.</div>
            </div>

            <div className="ma-trend-box">
              <div className="ma-trend-box-header">
                <span className="ma-trend-icon">⚡</span>
                <span className="ma-trend-title">Direct UPI Ubiquity</span>
              </div>
              <p className="ma-trend-desc">
                90%+ of Tier-2/3 Indian consumers now transact seamlessly on PhonePe, Google Pay,
                and Paytm. Customers expect frictionless 1-tap UPI without typing 16-digit card
                numbers or net banking passwords.
              </p>
              <div className="ma-trend-stat">Ownezzy Fix: Instant PhonePe/GPay intent triggers with zero delays.</div>
            </div>

            <div className="ma-trend-box">
              <div className="ma-trend-box-header">
                <span className="ma-trend-icon">🛑</span>
                <span className="ma-trend-title">Marketplace Disillusionment</span>
              </div>
              <p className="ma-trend-desc">
                Amazon, Flipkart, and Meesho charge 15%–30% in commission, withhold payouts for 7–14 days,
                and hide customer contact details, leaving sellers vulnerable to fake returns and copycat sellers.
              </p>
              <div className="ma-trend-stat">Ownezzy Fix: 0% commission, direct-to-bank settlements, 100% customer ownership.</div>
            </div>

            <div className="ma-trend-box">
              <div className="ma-trend-box-header">
                <span className="ma-trend-icon">👑</span>
                <span className="ma-trend-title">Regional Craft Pride</span>
              </div>
              <p className="ma-trend-desc">
                Shoppers crave authentic local items: Kanchipuram silk, Aari bridal blouses,
                Kumbakonam brassware, Jaipur block prints, and custom flower resin clocks. Buyers
                distrust anonymous marketplaces and prefer branded regional boutiques.
              </p>
              <div className="ma-trend-stat">Ownezzy Fix: Dedicated regional themes tailored to exact artisan crafts.</div>
            </div>
          </div>
        </section>
      )}

      {/* 5. Section: Competitor Benchmarking Matrix */}
      {showSection("competitors") && (
        <section className="ma-section">
          <div className="ma-section-header">
            <div className="ma-section-title-wrap">
              <h3><span>⚔️</span> Competitor Benchmarking Matrix</h3>
              <p>Head-to-head comparison against global platforms, Indian SaaS, marketplaces, and manual WhatsApp selling.</p>
            </div>
          </div>

          <div className="ma-table-container">
            <table className="ma-matrix-table">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Monthly Cost</th>
                  <th>Commission</th>
                  <th>Setup Speed</th>
                  <th>Blank Canvas Risk</th>
                  <th>India UX (UPI / WhatsApp)</th>
                  <th>Vulnerability to Exploit</th>
                </tr>
              </thead>
              <tbody>
                {COMPETITORS.map((c) => (
                  <tr key={c.name} className={c.highlight ? "highlight-ownezzy" : ""}>
                    <td>
                      <b>{c.name}</b>
                      {c.highlight && <span className="ma-badge-hero">OUR PLATFORM</span>}
                    </td>
                    <td>{c.price}</td>
                    <td>
                      <span className={c.commission.includes("0%") ? "ma-pill-good" : "ma-pill-bad"}>
                        {c.commission}
                      </span>
                    </td>
                    <td>{c.setupTime}</td>
                    <td>
                      <span className={c.blankCanvas.includes("None") ? "ma-pill-good" : "ma-pill-bad"}>
                        {c.blankCanvas}
                      </span>
                    </td>
                    <td>{c.indiaUx}</td>
                    <td>
                      <span className={c.highlight ? "ma-pill-warn" : "ma-pill-good"}>
                        {c.vulnerability}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* 6. Section: Strengths & Weaknesses (SWOT) */}
      {showSection("swot") && (
        <section className="ma-section">
          <div className="ma-section-header">
            <div className="ma-section-title-wrap">
              <h3><span>⚖️</span> Ownezzy Core Strengths vs. Weak Points</h3>
              <p>An honest evaluation of our competitive moats and operational bottlenecks.</p>
            </div>
          </div>

          <div className="ma-swot-grid">
            {/* Strong Points */}
            <div className="ma-swot-card strong">
              <div className="ma-swot-card-header">
                <h4>💪 Core Strong Points (Unfair Advantages)</h4>
                <span className="ma-pill-good">5 Major Moats</span>
              </div>
              <ul className="ma-swot-list">
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">✨</span>
                  <div className="ma-swot-item-content">
                    <b>Zero &ldquo;Blank Canvas&rdquo; Problem:</b>
                    <span>20 ready-to-sell niche templates (Aari blouses, brass urulis, pet treats, resin art) with pre-loaded photos, banners, and variants. No empty screens.</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">💸</span>
                  <div className="ma-swot-item-content">
                    <b>0% Commission &amp; Instant UPI Settlement:</b>
                    <span>Customer payments land directly in the seller&rsquo;s UPI bank account. Zero hold-up, zero deductions, 100% margin retention.</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">🚀</span>
                  <div className="ma-swot-item-content">
                    <b>Ultra-Lean Infrastructure Economics:</b>
                    <span>Lightweight Node.js + MongoDB multi-tenant stack running on Oracle Cloud ARM64 with Caddy on-demand SSL and Cloudflare R2. Cost-per-store is only ~₹10/month.</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">🤝</span>
                  <div className="ma-swot-item-content">
                    <b>Franchise &amp; Sub-Admin Reseller Engine:</b>
                    <span>Built-in agency partner portal enabling local digital freelancers, students, and agencies across Tier-2/3 cities to onboard shops in bulk and earn commissions.</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">📱</span>
                  <div className="ma-swot-item-content">
                    <b>Friction-Free Indian Psychology UX:</b>
                    <span>Phone OTP login (no passwords required for shoppers), COD toggle, WhatsApp order notifications, and regional context copy.</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Weak Points */}
            <div className="ma-swot-card weak">
              <div className="ma-swot-card-header">
                <h4>⚠️ Weak Points Against Competitors (Gaps to Fix)</h4>
                <span className="ma-pill-bad">5 Priority Fixes</span>
              </div>
              <ul className="ma-swot-list">
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">📦</span>
                  <div className="ma-swot-item-content">
                    <b>No Automated Courier / Shipping API:</b>
                    <span>Sellers currently enter courier tracking details manually. Competitors like Dukaan and Shopify offer 1-click Shiprocket/Delhivery label generation and doorstep pickup.</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">📲</span>
                  <div className="ma-swot-item-content">
                    <b>Web Admin Only (No Native Mobile App):</b>
                    <span>85%+ of Indian home sellers operate exclusively on Android. Responsive web is functional, but lacks a Google Play Store app with a loud &ldquo;cha-ching&rdquo; order ring.</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">🏦</span>
                  <div className="ma-swot-item-content">
                    <b>Payment Gateway KYC Friction:</b>
                    <span>Requiring individual Razorpay credentials with business KYC stops unregistered homemakers, students, and artisans who sell informally.</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">🧩</span>
                  <div className="ma-swot-item-content">
                    <b>Closed Monolithic Ecosystem:</b>
                    <span>No 3rd-party app marketplace or integrations for automated Meta Pixel syncing, WhatsApp CRM chatbots, or accounting software (Tally/Vyapar).</span>
                  </div>
                </li>
                <li className="ma-swot-item">
                  <span className="ma-swot-item-icon">📢</span>
                  <div className="ma-swot-item-content">
                    <b>Brand Awareness &amp; Social Proof Deficit:</b>
                    <span>Shopify and Dukaan are household names with massive ad spend. Ownezzy currently operates on bootstrapped organic outreach and word-of-mouth.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* 7. Section: Improvement Points & Concrete Technical Solutions */}
      {showSection("improvements") && (
        <section className="ma-section">
          <div className="ma-section-header">
            <div className="ma-section-title-wrap">
              <h3><span>🛠️</span> Strategic Improvement Points &amp; Solutions</h3>
              <p>Actionable technical and operational blueprints to eliminate our weaknesses and outperform competitors.</p>
            </div>
          </div>

          <div className="ma-solutions-grid">
            <div className="ma-solution-box">
              <div className="ma-solution-top">
                <h4><span>🚚</span> 1-Click Shipping Aggregator API</h4>
                <span className="ma-badge-priority high">Critical</span>
              </div>
              <div className="ma-solution-problem">Problem: Manual shipping creates friction and delays dispatching.</div>
              <div className="ma-solution-fix">
                <b>Solution:</b> Connect Shiprocket &amp; NimbusPost REST APIs. When an order arrives, seller taps &ldquo;Book Courier&rdquo; to auto-generate shipping label, assign AWB, and schedule doorstep pickup.
              </div>
              <div className="ma-solution-impact">
                <span>📈 Impact:</span> Reduces order fulfillment time from 15 minutes to 30 seconds.
              </div>
            </div>

            <div className="ma-solution-box">
              <div className="ma-solution-top">
                <h4><span>🔔</span> Android App + Order Ring Sound</h4>
                <span className="ma-badge-priority high">High Priority</span>
              </div>
              <div className="ma-solution-problem">Problem: Sellers miss order emails or dashboard notifications while busy.</div>
              <div className="ma-solution-fix">
                <b>Solution:</b> Wrap the admin panel in a lightweight Android TWA (Trusted Web Activity) with native WebPush alarm audio (&ldquo;Cash Register Bell&rdquo;) that rings loudly when an order is received.
              </div>
              <div className="ma-solution-impact">
                <span>📈 Impact:</span> Skyrockets seller engagement and daily active dashboard logins.
              </div>
            </div>

            <div className="ma-solution-box">
              <div className="ma-solution-top">
                <h4><span>⚡</span> Dynamic UPI QR (No KYC Required)</h4>
                <span className="ma-badge-priority high">High Priority</span>
              </div>
              <div className="ma-solution-problem">Problem: Home entrepreneurs cannot complete Razorpay business KYC.</div>
              <div className="ma-solution-fix">
                <b>Solution:</b> Provide dynamic UPI QR codes (via Cashfree/PhonePe Gateway partner routing or static VPA intent) so any seller can accept UPI payments directly into their personal savings account.
              </div>
              <div className="ma-solution-impact">
                <span>📈 Impact:</span> Unlocks onboarding for 10M+ unregistered Indian home sellers.
              </div>
            </div>

            <div className="ma-solution-box">
              <div className="ma-solution-top">
                <h4><span>💬</span> 15-Minute WhatsApp Cart Recovery</h4>
                <span className="ma-badge-priority medium">Revenue Booster</span>
              </div>
              <div className="ma-solution-problem">Problem: Up to 65% of checkout visitors drop off before paying.</div>
              <div className="ma-solution-fix">
                <b>Solution:</b> When a shopper inputs their phone number but doesn&rsquo;t complete checkout within 15 minutes, send an automated WhatsApp message with a 1-tap direct checkout link.
              </div>
              <div className="ma-solution-impact">
                <span>📈 Impact:</span> Recovers 18%–25% of lost revenue automatically with zero ad spend.
              </div>
            </div>

            <div className="ma-solution-box">
              <div className="ma-solution-top">
                <h4><span>📸</span> Instagram Profile Catalog Importer</h4>
                <span className="ma-badge-priority medium">Growth Hook</span>
              </div>
              <div className="ma-solution-problem">Problem: Uploading 20 photos and descriptions manually takes 1 hour.</div>
              <div className="ma-solution-fix">
                <b>Solution:</b> Allow sellers to submit their Instagram handle or upload a product sheet/WhatsApp catalog to auto-generate their product catalog with AI titles and tags.
              </div>
              <div className="ma-solution-impact">
                <span>📈 Impact:</span> Drops time-to-first-product to under 2 minutes.
              </div>
            </div>

            <div className="ma-solution-box">
              <div className="ma-solution-top">
                <h4><span>🤝</span> Franchise Reseller Referral Boost</h4>
                <span className="ma-badge-priority medium">Viral Scale</span>
              </div>
              <div className="ma-solution-problem">Problem: Digital advertising CAC can fluctuate wildly across seasons.</div>
              <div className="ma-solution-fix">
                <b>Solution:</b> Empower student freelancers and local web designers to earn 30% recurring commission on every store they sell in local shopping markets (Commercial Street, T. Nagar, Chickpet).
              </div>
              <div className="ma-solution-impact">
                <span>📈 Impact:</span> Establishes a zero-CAC, boots-on-the-ground regional sales army.
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 8. Section: Immediate Action Items (Phased Checklist) */}
      {showSection("actions") && (
        <section className="ma-section">
          <div className="ma-section-header">
            <div className="ma-section-title-wrap">
              <h3><span>✅</span> Immediate Action Items &amp; Execution Checklist</h3>
              <p>Prioritized engineering and growth deliverables to execute over the next 60 days.</p>
            </div>
            <span className="ma-pill-good">Live Execution Roadmap</span>
          </div>

          <div className="ma-checklist-container">
            {ACTION_PHASES.map((group) => (
              <div key={group.phase} className="ma-phase-group">
                <div className="ma-phase-header">
                  <div className="ma-phase-title">
                    <span>🎯</span>
                    <span>{group.phase}</span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <span className="ma-phase-tag">{group.timing}</span>
                    <span className="ma-phase-tag" style={{ background: "#e0f2fe", color: "#0369a1" }}>{group.tag}</span>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {group.items.map((item) => {
                    const isChecked = checklist[item.key] ?? item.defaultChecked;
                    return (
                      <div
                        key={item.key}
                        className={`ma-task-item ${isChecked ? "checked" : ""}`}
                        onClick={() => handleToggleTask(item.key)}
                      >
                        <input
                          type="checkbox"
                          className="ma-checkbox"
                          checked={isChecked}
                          onChange={() => {}} // handled by parent div
                          aria-label={item.title}
                        />
                        <div className="ma-task-content">
                          <span className="ma-task-title">{item.title}</span>
                          <span className="ma-task-desc">{item.desc}</span>
                        </div>
                        <span className="ma-task-badge">{item.badge}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 9. Section: Strategic Recommendations & Suggestions From Our Side */}
      {showSection("recommendations") && (
        <section className="ma-section">
          <div className="ma-section-header">
            <div className="ma-section-title-wrap">
              <h3><span>💡</span> Strategic Recommendations &amp; Advice from Our Side</h3>
              <p>High-leverage growth strategies curated specifically for Ownezzy&rsquo;s current stage.</p>
            </div>
            <span className="ma-pill-good">Executive Advisory</span>
          </div>

          <div className="ma-recs-grid">
            <div className="ma-rec-card">
              <div className="ma-rec-header">
                <span className="ma-rec-number">1</span>
                <span className="ma-rec-title">Hyper-Focus on 4 Goldmine Niches</span>
              </div>
              <p className="ma-rec-body">
                Do not advertise to generic &ldquo;Indian businesses&rdquo;. Target the 4 high-margin,
                high-intent clusters where DM fatigue is unbearable:
                <b> 1) South India Aari/Maggam Blouses</b>, <b>2) Wedding Resin Clocks &amp; Favors</b>,
                <b> 3) Surat/Jaipur Kurtis</b>, and <b>4) Regional Artisanal Brassware</b>.
              </p>
              <div className="ma-rec-highlight">
                📍 Strategy: High average order value (₹1,500–₹5,000) makes the ₹299/mo subscription an obvious no-brainer.
              </div>
            </div>

            <div className="ma-rec-card">
              <div className="ma-rec-header">
                <span className="ma-rec-number">2</span>
                <span className="ma-rec-title">Laser Deep-Linking to Live Demo Stores</span>
              </div>
              <p className="ma-rec-body">
                Never direct Instagram Reels ad traffic to the generic homepage (ownezzy.com). Always send
                ad clicks directly to the specialized live demo store (e.g., <code>blouses.ownezzy.com</code>).
                The automatic 10-second conversion modal will capture 3x higher sign-ups than any landing page.
              </p>
              <div className="ma-rec-highlight">
                📍 Strategy: Seeing their exact boutique niche ready-to-use converts cold visitors immediately.
              </div>
            </div>

            <div className="ma-rec-card">
              <div className="ma-rec-header">
                <span className="ma-rec-number">3</span>
                <span className="ma-rec-title">ARR Lock-In with Annual Bundles</span>
              </div>
              <p className="ma-rec-body">
                Monthly micro-SaaS subscriptions in India suffer from 12%–18% monthly churn due to UPI recurring mandate failures.
                Combat this by heavily incentivizing the <b>Annual Plan at ₹2,999/year</b> (saves ₹1,000 + free custom domain linking).
              </p>
              <div className="ma-rec-highlight">
                📍 Strategy: Secures 12 months of upfront cash flow to self-fund paid marketing acquisition.
              </div>
            </div>

            <div className="ma-rec-card">
              <div className="ma-rec-header">
                <span className="ma-rec-number">4</span>
                <span className="ma-rec-title">Viral K-Factor Footers on All Stores</span>
              </div>
              <p className="ma-rec-body">
                Every store created on Ownezzy should feature a discreet badge in the footer:
                <em> &ldquo;Powered by Ownezzy · Get this website free in 60s&rdquo;</em>.
                When neighboring shop owners order or view a friend&rsquo;s store, 1-tap clones the template.
              </p>
              <div className="ma-rec-highlight">
                📍 Strategy: Turns every active merchant into an organic acquisition channel for zero ad spend.
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
